import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './EC2LoadingBar.css';
import { baseApi } from '../../../../API/api';
import { useEC2Loading } from '../../../../contexts/EC2LoadingContext';
import { useEC2InstanceStatus } from '../../../../contexts/EC2InstanceStatusContext';
import authService from '../../../../services/authService';
import pixelStreamingProgressService from '../../../../services/pixelStreamingProgressService';

const EC2LoadingBar = ({ projectId, onComplete, onError, onStopping, onSuccess }) => {
  const { getInstanceStatus } = useEC2InstanceStatus();
  const [currentStep, setCurrentStep] = useState('checking');
  const [statusMessageState, setStatusMessageState] = useState('3D Viewer is starting...');
  const [progress, setProgress] = useState(0);
  const pollTimeoutRef = useRef(null);
  const readyTimeoutRef = useRef(null);
  const threeMinuteCheckRef = useRef(null);
  const successTimeoutRef = useRef(null);
  const shouldStopPollingRef = useRef(false);
  const hasTriggeredNavigationRef = useRef(false);
  const inSuccessStateRef = useRef(false); // Track if we're in success state
  const targetProgressRef = useRef(0); // Target progress value for smooth animation
  const animationFrameRef = useRef(null); // Animation frame ID
  const animationStartProgressRef = useRef(0); // Starting progress for animation
  const animationStartTimeRef = useRef(null); // Animation start time
  const initialProgressStartTimeRef = useRef(null); // Time when initial progress started
  const initialProgressIntervalRef = useRef(null); // Interval for initial 0-25% progress
  const pixelStreamingDataReceivedRef = useRef(false); // Track if pixel streaming data has been received
  const abortControllerRef = useRef(null); // AbortController for canceling requests
  const unsubscribeRef = useRef(null); // Track subscription to prevent duplicates
  
  // Track last POST status message to prevent going backwards
  const lastPostStatusRef = useRef(null);
  const lastPostStatusMessageRef = useRef(null);
  
  // Protected status message setter that prevents overwriting POST messages
  const setStatusMessage = (message, source = 'unknown') => {
    // If pixel streaming data has been received, protect POST messages from being overwritten
    if (pixelStreamingDataReceivedRef.current) {
      // Block generic EC2 polling messages that would overwrite POST messages
      const blockedMessages = [
        '3D Viewer is starting...',
        'Launching 3D Viewer...',
        'Restarting 3D Viewer launch...'
      ];
      
      // Always block generic messages from EC2 polling (unless they're from POST/success)
      if (blockedMessages.includes(message) && source !== 'post' && source !== 'success') {
        console.log(`🚫 Blocked status message "${message}" (source: ${source}) - pixel streaming data received`);
        return; // Don't overwrite POST messages with generic EC2 polling messages
      }
      
      // If we're in success state, only allow success-related messages
      if (inSuccessStateRef.current && message !== '3D viewer is active' && source !== 'post') {
        console.log(`🚫 Blocked status message "${message}" - already in success state`);
        return;
      }
      
      // Always allow POST messages - they come from the EC2 instance and are authoritative
      if (source === 'post') {
        // Update and track the message (even if same, to ensure state is correct)
        lastPostStatusMessageRef.current = message;
        lastPostStatusRef.current = message; // Track the message itself
        setStatusMessageState(message);
        return;
      }
      
      // Always allow success message
      if (source === 'success' || message === '3D viewer is active') {
        setStatusMessageState(message);
        return;
      }
      
      // Allow error messages
      if (message.includes('Error:')) {
        setStatusMessageState(message);
        return;
      }
      
      // Allow context updates if they match the last POST message (prevents flickering)
      // Also allow same messages from any source (prevents unnecessary blocking)
      if (message === lastPostStatusMessageRef.current) {
        // Same message, no need to update (prevents flickering)
        return;
      }
      
      // Block all other messages once POST messages have started
      // This prevents any non-POST messages from overwriting POST messages
      // BUT: Allow messages that are different and come from POST/context/success/error sources
      if (lastPostStatusMessageRef.current && 
          source !== 'post' && 
          source !== 'context' && 
          source !== 'success' && 
          !message.includes('Error:')) {
        console.log(`🚫 Blocked status message "${message}" (source: ${source}) - POST message active: "${lastPostStatusMessageRef.current}"`);
        return;
      }
    }
    
    // If pixel streaming data hasn't been received yet, allow all messages
    setStatusMessageState(message);
  };
  
  const statusMessage = statusMessageState;

  // Ref to track current animated progress value
  const currentAnimatedProgressRef = useRef(0);

  // Smooth progress animation function
  const animateProgress = (targetProgress, duration = 1500, onComplete = null) => {
    // Cancel any existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Get current progress from ref (which tracks the animated value)
    const startProgress = currentAnimatedProgressRef.current || progress;
    const startTime = performance.now();
    animationStartProgressRef.current = startProgress;
    animationStartTimeRef.current = startTime;
    targetProgressRef.current = Math.min(Math.max(targetProgress, 0), 100); // Clamp between 0-100

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progressRatio = Math.min(elapsed / duration, 1); // 0 to 1

      // Easing function for smooth animation (ease-out)
      const easeOut = 1 - Math.pow(1 - progressRatio, 3);

      // Calculate current progress
      const animatedProgress = startProgress + (targetProgressRef.current - startProgress) * easeOut;

      // Update both ref and state
      currentAnimatedProgressRef.current = animatedProgress;
      setProgress(animatedProgress);

      // Continue animation if not complete
      if (progressRatio < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete, ensure we're at exact target
        currentAnimatedProgressRef.current = targetProgressRef.current;
        setProgress(targetProgressRef.current);
        animationFrameRef.current = null;
        if (typeof onComplete === 'function') {
          onComplete();
        }
      }
    };

    // Start animation
    animationFrameRef.current = requestAnimationFrame(animate);
  };

  // Function to update progress with smooth animation
  const updateProgressSmoothly = (newProgress, onComplete = null) => {
    // Clamp progress between 0-100%
    const clampedProgress = Math.max(0, Math.min(100, newProgress));
    
    // Get current progress from ref
    const currentProgress = currentAnimatedProgressRef.current || progress;
    const progressDiff = Math.abs(clampedProgress - currentProgress);
    
    // Only animate if the change is significant (more than 1%) or if we're moving forward
    if (progressDiff > 1 || clampedProgress > currentProgress) {
      animateProgress(clampedProgress, 1500, onComplete); // 1.5 second animation
    } else {
      // Small change, update directly
      currentAnimatedProgressRef.current = clampedProgress;
      targetProgressRef.current = clampedProgress;
      setProgress(clampedProgress);
      if (typeof onComplete === 'function') {
        onComplete();
      }
    }
  };
  const { getLoadingProject, updateLoadingState, stopLoading } = useEC2Loading();
  const { isInstanceRunning } = useEC2InstanceStatus();
  
  // Get project info from context
  const projectInfo = getLoadingProject(projectId);
  const externalUrl = projectInfo?.externalUrl;

  // Track previous values to prevent unnecessary updates
  const prevValuesRef = useRef({ progress: null, statusMessage: null, currentStep: null });
  
  // Persist progress, statusMessage, and currentStep to context whenever they change
  useEffect(() => {
    // Only update if we have a valid loading state (not during initial mount before state is set)
    if (projectInfo && progress !== undefined) {
      const roundedProgress = Math.round(progress);
      
      // Only update if values actually changed
      const prev = prevValuesRef.current;
      if (
        prev.progress === roundedProgress &&
        prev.statusMessage === statusMessageState &&
        prev.currentStep === currentStep
      ) {
        return; // No changes, skip update
      }
      
      // Update previous values
      prevValuesRef.current = {
        progress: roundedProgress,
        statusMessage: statusMessageState,
        currentStep: currentStep
      };
      
      updateLoadingState(projectId, {
        progress: roundedProgress,
        statusMessage: statusMessageState,
        currentStep: currentStep
      });
    }
  }, [progress, statusMessageState, currentStep, projectId, projectInfo, updateLoadingState]);

  // Helper function to map raw status keys to formatted messages
  const mapStatusToMessage = (statusKey) => {
    const statusMessagesMap = {
      'system_starting': 'System starting...',
      'startup_script_launched': 'Launching services...',
      'build_completed': 'Build completed...',
      'server_initialized': 'Server initializing...',
      'server_port_verified': 'Server port verified...',
      'server_listening': 'Server listening...',
      'server_health_verified': 'Server health verified...',
      'server_ready': 'Server ready...',
      'websocket_fix_applied': 'Applying WebSocket fixes...',
      'unreal_engine_running': 'Unreal Engine starting...',
      'streamer_connected_verified': 'Streamer connection verified...',
      'streamer_connecting': 'Connecting streamer...',
      'streamer_connected': 'Streamer connected...',
      'streamer_ready': 'Streamer ready...',
      'authenticated': 'Authenticating...',
      'streamer_available': 'Streamer available...',
      'ready': '3D viewer is active'
    };
    return statusMessagesMap[statusKey] || statusKey;
  };

  // Sync local state with context updates from broadcasts (for real-time sync across users)
  // This ensures all users see the same progress as the launching user
  useEffect(() => {
    if (projectInfo) {
      const contextProgress = projectInfo.progress || 0;
      const contextStatusMessage = projectInfo.statusMessage || 'Launching 3D Viewer...';
      const contextCurrentStep = projectInfo.currentStep || 'checking';
      
      // Update local state if context has newer progress (from broadcasts)
      // Only update if context progress is higher to avoid going backwards
      if (contextProgress > targetProgressRef.current && contextProgress > progress) {
        console.log(`🔄 Syncing progress from context: ${targetProgressRef.current}% → ${contextProgress}% (broadcast update)`);
        targetProgressRef.current = contextProgress;
        updateProgressSmoothly(contextProgress);
        pixelStreamingDataReceivedRef.current = true; // Mark that we've received data
      }
      
      // Update status message if context has a different one (from broadcasts)
      // Context might have raw status keys (like "system_starting") that need to be mapped to formatted messages
      // Map raw status key to formatted message if needed
      const formattedContextMessage = mapStatusToMessage(contextStatusMessage);
      
      // Only update if it's not a generic EC2 polling message
      const genericMessages = ['Launching 3D Viewer...', '3D Viewer is starting...', 'Restarting 3D Viewer launch...'];
      const isGenericMessage = genericMessages.includes(formattedContextMessage);
      
      if (formattedContextMessage !== statusMessageState && !isGenericMessage) {
        // If pixel streaming data has been received, only update from context if it's a valid POST message
        // POST messages are typically short and don't contain "3D Viewer" (they're more specific like "System starting...")
        // Generic EC2 polling messages contain "3D Viewer" or "Launching"
        const isPostMessage = !formattedContextMessage.includes('3D Viewer') && 
                             !formattedContextMessage.includes('Launching') &&
                             formattedContextMessage !== 'Restarting 3D Viewer launch...';
        
        // Don't update status message from context - keep "3D Viewer is starting..." throughout
        // Only update progress, not the message
      }
      
      // Update current step if context has a different one (from broadcasts)
      if (contextCurrentStep !== currentStep) {
        setCurrentStep(contextCurrentStep);
      }
    }
  }, [projectInfo?.progress, projectInfo?.statusMessage, projectInfo?.currentStep, projectId, progress, statusMessageState, currentStep]);

  const steps = [
    { key: 'checking', label: 'Checking instance status...', duration: 10, progress: 5 },
    { key: 'starting', label: 'Starting EC2 instance...', duration: 60, progress: 20 },
    { key: 'booting', label: 'Instance booting up...', duration: 120, progress: 40 },
    { key: 'health_check', label: 'Running health checks...', duration: 60, progress: 60 },
    { key: 'js_server', label: 'Starting JS server...', duration: 120, progress: 90 },
    { key: 'OK', label: 'Almost ready...', duration: 10, progress: 100 }
  ];

  // Function to restart instance launch (called when instance check fails)
  const restartInstanceLaunch = async () => {
    // Clear all timeouts and intervals
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (readyTimeoutRef.current) {
      clearTimeout(readyTimeoutRef.current);
      readyTimeoutRef.current = null;
    }
    if (threeMinuteCheckRef.current) {
      clearTimeout(threeMinuteCheckRef.current);
      threeMinuteCheckRef.current = null;
    }

    // Reset progress to 0
    setProgress(0);
    currentAnimatedProgressRef.current = 0;
    targetProgressRef.current = 0;
    pixelStreamingDataReceivedRef.current = false;
    
    // Cancel any ongoing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (initialProgressIntervalRef.current) {
      clearInterval(initialProgressIntervalRef.current);
      initialProgressIntervalRef.current = null;
    }
    
    updateLoadingState(projectId, { 
      state: 'checking',
      isFreshLaunch: true // Mark as fresh launch to trigger API call
    });

    // Start instance startup again
    setStatusMessage('Restarting 3D Viewer launch...');
    setCurrentStep('checking');
    
    // Start fresh (not restoring)
    await startInstanceStartup(false);
  };

  // Initialize progress with time-based 0-25% over 70 seconds
  useEffect(() => {
    // Check if we're restoring from persisted state - restore progress if available
    const isRestoring = projectInfo?.startTime && projectInfo?.state && !projectInfo?.isFreshLaunch;
    const restoredProgress = projectInfo?.progress || 0;
    const restoredCurrentStep = projectInfo?.currentStep || 'checking';
    
    // Always set status message to "3D Viewer is starting..." (don't show POST payload messages)
    setStatusMessage('3D Viewer is starting...');
    
    // If restoring and we have progress > 0, restore it immediately
    // BUT: Don't restore 100% progress - it might be stale. Reset to 0 and wait for fresh SSE data
    if (isRestoring && restoredProgress > 0 && restoredProgress < 100) {
      setProgress(restoredProgress);
      currentAnimatedProgressRef.current = restoredProgress;
      targetProgressRef.current = restoredProgress;
      setCurrentStep(restoredCurrentStep);
      // Mark that we've received pixel streaming data if progress > 0
      if (restoredProgress > 0) {
        pixelStreamingDataReceivedRef.current = true;
      }
      // Don't restore success state from localStorage - let SSE stream confirm if actually ready
      // This prevents premature overlay click when EC2 is restarting with stale cached data
      // Overlay click will only happen when we receive 100% + ready=true from SSE stream
      console.log(`🔄 Restoring progress for project ${projectId}: ${restoredProgress}%`);
    } else {
      // Fresh launch OR restoring 100% (stale) - reset state
      setProgress(0);
      currentAnimatedProgressRef.current = 0;
      targetProgressRef.current = 0;
      pixelStreamingDataReceivedRef.current = false;
      inSuccessStateRef.current = false;
      hasTriggeredNavigationRef.current = false;
      lastPostStatusMessageRef.current = null; // Clear last POST message
      if (isRestoring && restoredProgress >= 100) {
        console.log(`🔄 Ignoring stale 100% progress for project ${projectId} - waiting for fresh SSE data`);
      }
    }
    
    initialProgressStartTimeRef.current = Date.now();

    // Start time-based progress: 0% to 25% over 70 seconds (only if not restoring or progress is 0)
    if (!isRestoring || restoredProgress === 0) {
      const INITIAL_PROGRESS_DURATION = 70000; // 70 seconds
      const INITIAL_PROGRESS_TARGET = 25; // 25%
      
      initialProgressIntervalRef.current = setInterval(() => {
        // Only update if pixel streaming data hasn't been received yet
        if (!pixelStreamingDataReceivedRef.current && initialProgressStartTimeRef.current) {
          const elapsed = Date.now() - initialProgressStartTimeRef.current;
          const timeBasedProgress = Math.min((elapsed / INITIAL_PROGRESS_DURATION) * INITIAL_PROGRESS_TARGET, INITIAL_PROGRESS_TARGET);
          
          // Update progress smoothly
          targetProgressRef.current = timeBasedProgress;
          updateProgressSmoothly(timeBasedProgress);
          
          // Stop interval once we reach 25% or if pixel streaming data is received
          if (timeBasedProgress >= INITIAL_PROGRESS_TARGET || pixelStreamingDataReceivedRef.current) {
            if (initialProgressIntervalRef.current) {
              clearInterval(initialProgressIntervalRef.current);
              initialProgressIntervalRef.current = null;
            }
          }
        }
      }, 100); // Update every 100ms for smooth animation
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (initialProgressIntervalRef.current) {
        clearInterval(initialProgressIntervalRef.current);
        initialProgressIntervalRef.current = null;
      }
    };
  }, [projectId]);

  // Watch for progress reaching 100% to show success message
  // NOTE: Success is now ONLY controlled by the SSE callback which checks for ready=true
  // This prevents premature success from restored state or EC2 instance status checks
  // The useEffect is kept for cleanup but no longer triggers success automatically
  useEffect(() => {
    // Success is now only shown when SSE stream confirms progress >= 100% AND ready === true
    // This effect no longer shows success automatically to prevent false positives
    // from restored localStorage data or EC2 instance status checks
    
    // Cleanup function - no action needed here as success is handled by SSE callback

    // Don't clear timeout in cleanup if we're in success state - let it fire naturally
    return () => {
      // Only clear if we're not in success state yet (to prevent setting multiple timeouts)
      // Use ref instead of state to get current value in cleanup
      if (!inSuccessStateRef.current && successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
      // If in success state, don't clear - overlay click already triggered
    };
  }, [progress, projectId, stopLoading, onComplete]);

  // Subscribe to pixel streaming progress updates (using shared service to prevent duplicate polling)
  useEffect(() => {
    // Unsubscribe any existing subscription first
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // Don't subscribe if we're already in success state or if progress reached 100%
    if (targetProgressRef.current >= 100 || shouldStopPollingRef.current) {
      return;
    }

    // Status message mapping - maps backend status to user-friendly messages
    const statusMessages = {
      // System Startup (1-30%)
      'system_starting': 'System starting...',
      'startup_script_launched': 'Launching services...',
      'build_completed': 'Build completed...',
      'server_initialized': 'Server initializing...',
      'server_port_verified': 'Server port verified...',
      'server_listening': 'Server listening...',
      'server_health_verified': 'Server health verified...',
      'server_ready': 'Server ready...',
      
      // Setup Tasks (40-60%)
      'websocket_fix_applied': 'Applying WebSocket fixes...',
      'unreal_engine_running': 'Unreal Engine starting...',
      'streamer_connected_verified': 'Streamer connection verified...',
      'streamer_connecting': 'Connecting streamer...',
      'streamer_connected': 'Streamer connected...',
      'streamer_ready': 'Streamer ready...',
      
      // Client Connection (20-95%)
      'authenticated': 'Authenticating...',
      'streamer_available': 'Streamer available...',
      'webrtc_negotiating': 'Negotiating connection...',
      'webrtc_connecting': 'Connecting...',
      'webrtc_connected': 'Connection established...',
      'video_initialized': 'Initializing video...',
      
      // Final Verification (90-100%)
      'verifying_stream': 'Verifying stream...',
      'stream_verified': 'Stream verified...',
      'ready': '3D viewer is active',
      
      // Error Statuses
      'error': 'Error occurred...',
      'build_failed': 'Build failed...',
      'server_port_failed': 'Server port failed...',
      'server_health_failed': 'Server health check failed...',
      'unreal_engine_failed': 'Unreal Engine failed...',
      'streamer_connection_failed': 'Streamer connection failed...',
      'player_subscription_timeout': 'Connection timeout...',
      'step_completed': 'Processing...'
    };

    // Subscribe to progress updates via shared service (prevents duplicate polling)
    unsubscribeRef.current = pixelStreamingProgressService.subscribe(projectId, (pixelProgress, pixelStatus, isReady) => {
      // Handle error status
      if (pixelStatus === 'error') {
        setStatusMessage('Connection issues detected. Progress updates paused.');
        return;
      }

      // Ignore 'not_received' status
      if (pixelProgress === 0 && pixelStatus === 'not_received') {
        return;
      }

      // If we receive any valid progress data, switch from time-based to POST data
      if (pixelProgress > 0 && pixelStatus !== 'not_received' && !pixelStreamingDataReceivedRef.current) {
        pixelStreamingDataReceivedRef.current = true;
        if (initialProgressIntervalRef.current) {
          clearInterval(initialProgressIntervalRef.current);
          initialProgressIntervalRef.current = null;
        }
        console.log(`🎬 ✅ Pixel streaming data received for project ${projectId}: ${pixelProgress}% (${pixelStatus})`);
        // Stop EC2 polling from setting status messages
        shouldStopPollingRef.current = false; // Keep polling for readiness, but status messages are now from POST
      }

      // Get current progress
      const currentProgress = targetProgressRef.current || progress;
      
      // Check if this is an error status
      const errorStatuses = ['error', 'build_failed', 'server_port_failed', 'server_health_failed', 
                             'unreal_engine_failed', 'streamer_connection_failed', 'player_subscription_timeout'];
      const isErrorStatus = pixelStatus && errorStatuses.includes(pixelStatus);
      
      // For error statuses, show error message (override the static message)
      if (isErrorStatus) {
        if (pixelStatus && statusMessages[pixelStatus]) {
          const errorMessage = statusMessages[pixelStatus];
          lastPostStatusRef.current = pixelStatus;
          lastPostStatusMessageRef.current = errorMessage;
          setStatusMessage(errorMessage, 'post');
        }
        return;
      }
      
      // Process progress updates
      if (pixelProgress > 0 && pixelStatus !== 'not_received') {
        const finalTarget = Math.min(Math.max(pixelProgress, 0), 100);
        
        // Update if new progress is higher than current
        if (finalTarget > currentProgress || (finalTarget >= 100 && currentProgress >= 100)) {
          targetProgressRef.current = finalTarget;
          updateProgressSmoothly(finalTarget);

          // DON'T update status message from POST payload - keep "3D Viewer is starting..." throughout
          // Only track the status for logging purposes
          if (pixelStatus && statusMessages[pixelStatus]) {
            const postMessage = statusMessages[pixelStatus];
            lastPostStatusRef.current = pixelStatus;
            lastPostStatusMessageRef.current = postMessage;
            // Don't call setStatusMessage - keep the static "3D Viewer is starting..." message
          }

          console.log(`🎬 Pixel streaming progress for project ${projectId}: ${pixelProgress}% (${pixelStatus})`);
        }
      }

      // When progress reaches 100% AND ready flag is true, immediately trigger overlay click
      // No success bar - just trigger the overlay click directly
      // IMPORTANT: Only trigger if progress is EXACTLY 100 (not less) to prevent premature navigation
      if (pixelProgress >= 100 && isReady === true && !shouldStopPollingRef.current && !hasTriggeredNavigationRef.current) {
        // Double-check: progress must be exactly 100, not less
        if (pixelProgress < 100) {
          console.warn(`⚠️ Blocked premature navigation: progress=${pixelProgress}% (expected 100%), ready=${isReady}`);
          return;
        }
        
        console.log(`🎬 Pixel streaming progress reached 100% and ready=true for project ${projectId}!`);
        console.log(`Navigation will trigger from SSE 100% readiness signal...`);
        
        shouldStopPollingRef.current = true;
        hasTriggeredNavigationRef.current = true;
        
        // Clear all timeouts
        if (pollTimeoutRef.current) {
          clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = null;
        }
        if (readyTimeoutRef.current) {
          clearTimeout(readyTimeoutRef.current);
          readyTimeoutRef.current = null;
        }
        if (threeMinuteCheckRef.current) {
          clearTimeout(threeMinuteCheckRef.current);
          threeMinuteCheckRef.current = null;
        }
        if (successTimeoutRef.current) {
          clearTimeout(successTimeoutRef.current);
          successTimeoutRef.current = null;
        }
        setStatusMessage('3D viewer is active', 'success');
        setCurrentStep('success');
        inSuccessStateRef.current = true;
        
        // Keep the bar moving for UX, but do NOT gate navigation on animation completion.
        updateProgressSmoothly(100);

        // Redirect is tied to SSE 100% + ready=true, not the visual bar animation.
        successTimeoutRef.current = setTimeout(() => {
          if (onSuccess) {
            console.log(`Triggering navigation for project ${projectId} from SSE readiness`);
            onSuccess();
          } else {
            // No-op: caller decides whether to navigate.
            console.log(`No onSuccess handler provided for project ${projectId}; skipping redirect`);
          }

          if (onComplete) {
            onComplete();
          }

          // Clear loading state only after callbacks so initiator checks can still read context.
          stopLoading(projectId);
        }, 50);
      }
    });

    return () => {
      // Unsubscribe when component unmounts or effect re-runs
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
    // Only depend on projectId - other values are checked via refs/early returns
    // This prevents unnecessary re-subscriptions when progress/status changes
  }, [projectId]);

  useEffect(() => {
    // Don't restart if we're already in success state (progress >= 100%)
    if (progress >= 100) {
      return;
    }
    
    // Reset polling stop flag and success state ref when component mounts or projectId changes
    shouldStopPollingRef.current = false;
    inSuccessStateRef.current = false;
    hasTriggeredNavigationRef.current = false;
    
    // Check if we're restoring from persisted state (has startTime and state, but NOT isFreshLaunch)
    const isRestoring = projectInfo?.startTime && projectInfo?.state && !projectInfo?.isFreshLaunch;
    
    // If this is a fresh launch, clear the flag after starting
    if (projectInfo?.isFreshLaunch) {
      updateLoadingState(projectId, { isFreshLaunch: false });
    }
    
    // Check for temporary message from re-click during launch
    if (projectInfo?.tempMessage) {
      setStatusMessage(projectInfo.tempMessage);
      // Clear temp message after 3 seconds
      setTimeout(() => {
        updateLoadingState(projectId, { tempMessage: null });
        startInstanceStartup(isRestoring);
      }, 3000);
    } else {
      startInstanceStartup(isRestoring);
    }
    
      // Cleanup function to clear any pending timeouts
      return () => {
        if (pollTimeoutRef.current) {
          clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = null;
        }
        if (readyTimeoutRef.current) {
          clearTimeout(readyTimeoutRef.current);
          readyTimeoutRef.current = null;
        }
        if (threeMinuteCheckRef.current) {
          clearTimeout(threeMinuteCheckRef.current);
          threeMinuteCheckRef.current = null;
        }
      };
  }, [projectId, projectInfo?.isFreshLaunch, projectInfo?.tempMessage, projectInfo?.startTime, projectInfo?.state]);


  // Number of times we silently retry the start call when AWS reports no GPU
  // capacity (after the backend has already tried every instance type in its
  // fallback ladder). Capacity churns second-to-second, so a short wait + retry
  // usually lands a slot without the user ever seeing an error.
  const MAX_CAPACITY_RETRIES = 6;
  const CAPACITY_RETRY_DELAY_MS = 8000;

  const startInstanceStartup = async (isRestoring = false, capacityRetry = 0) => {
    try {
      // If restoring from persisted state, skip the start API call and go straight to polling
      if (!isRestoring && !shouldStopPollingRef.current) {
        setStatusMessage('Launching 3D Viewer...');
        setCurrentStep('checking');
        
        // Start the instance startup process (non-blocking)
        // We'll check status through polling to get accurate real-time state
        setStatusMessage('Launching 3D Viewer...');
        setCurrentStep('starting');
        
        const startResponse = await axios.post(`${baseApi}/api/ec2/start/${projectId}`, {});
        const startResult = startResponse.data;
        
        if (!startResult.success) {
          // Handle errors
          if (startResult.error === 'Instance is shutting down' || startResult.message?.includes('shutting down')) {
            // Show stopping message and keep loading bar visible
            setStatusMessage('3D Viewer is shutting down. Please wait a few minutes for it to fully stop, then try again.');
            setCurrentStep('stopping');
            setProgress(0); // Reset progress since we can't start
            
            // Keep the loading bar visible for 10 seconds to show the message, then hide it
            setTimeout(() => {
              if (onStopping) {
                onStopping();
              } else {
                // If no onStopping handler, show error instead
                onError(new Error('Instance is shutting down. Please wait a few minutes and try again.'));
              }
            }, 10000); // Show message for 10 seconds
            return;
          }

          // No GPU capacity on ANY instance type in the backend ladder. Capacity
          // frees up constantly, so keep the loading bar up and silently retry a
          // few times before surfacing an error to the user.
          if (startResult.error_code === 'InsufficientInstanceCapacity' && capacityRetry < MAX_CAPACITY_RETRIES) {
            console.warn(`⏳ No GPU capacity for project ${projectId}; retry ${capacityRetry + 1}/${MAX_CAPACITY_RETRIES} in ${CAPACITY_RETRY_DELAY_MS}ms`);
            setStatusMessage('Waiting for an available 3D Viewer GPU...');
            setCurrentStep('starting');
            await new Promise((resolve) => setTimeout(resolve, CAPACITY_RETRY_DELAY_MS));
            if (shouldStopPollingRef.current) {
              return;
            }
            return startInstanceStartup(isRestoring, capacityRetry + 1);
          }

          // Create error with backend response details
          const errorMessage = startResult.message || startResult.error || 'Failed to start 3D Viewer instance';
          const error = new Error(errorMessage);
          
          // Preserve all backend error details for better debugging
          error.backendResponse = startResult;
          error.projectId = projectId;
          if (startResult.troubleshooting) {
            error.troubleshooting = startResult.troubleshooting;
          }
          if (startResult.instance_id) {
            error.instanceId = startResult.instance_id;
          }
          if (startResult.instance_name) {
            error.instanceName = startResult.instance_name;
          }
          
          throw error;
        }

        // Update state to indicate we've started launching
        updateLoadingState(projectId, { state: 'launching' });
      } else {
        // Restoring state - resume polling from where we left off
        console.log(`🔄 Restoring EC2 loading state for project ${projectId}`);
        // Don't update status if we've already reached success state
        if (!shouldStopPollingRef.current) {
          setStatusMessage('3D Viewer is starting...');
          setCurrentStep(projectInfo?.state === 'ready' ? 'OK' : 'booting');
        }
      }

      // Don't trust the start response state - always poll to verify actual state
      // The start endpoint returns immediately and may not reflect the true current state
      
      // 3D Viewer is starting - poll for status to verify actual state
      // Don't update status if we've already reached success state
      if (!isRestoring && !shouldStopPollingRef.current) {
        setStatusMessage('3D Viewer is starting...');
        setCurrentStep('booting');
      }
      
      // Poll for instance readiness
      const maxPollAttempts = 120; // 20 minutes max (120 * 10 seconds)
      const pollInterval = 10000; // Poll every 10 seconds
      let pollAttempts = 0;
      let runningStateFirstSeen = null; // Track when we first see "running" state
      const MIN_RUNNING_DURATION = 3000; // Require 30 seconds of consistent "running" state before trusting it
      
      const pollForReady = async () => {
        try {
          // Stop polling if flag is set
          if (shouldStopPollingRef.current) {
            return;
          }
          
          pollAttempts++;
          
          // Check the actual instance state (AWS status)
          const checkResponse = await axios.get(`${baseApi}/api/ec2/check-ready/${projectId}`, {
            timeout: 30000
          });
          const checkResult = checkResponse.data;
          
          // Update progress based on elapsed time and actual instance state/status checks
          const elapsedMinutes = Math.floor((pollAttempts * pollInterval) / 60000);
          const currentState = checkResult.status?.state || 'unknown';
          const statusChecksPassed = checkResult.status?.status_checks_passed || 0;
          const isHealthy = checkResult.status?.is_healthy || false;
          const systemCheck = checkResult.status?.system_check || 'unknown';
          const instanceCheck = checkResult.status?.status_check || 'unknown';
          
          console.log(`🔍 Poll ${pollAttempts} for project ${projectId}: state=${currentState}, checks=${statusChecksPassed}/3, healthy=${isHealthy}, system=${systemCheck}, instance=${instanceCheck}`);
          
          // Don't update status message if we should stop polling (progress reached 100%)
          if (shouldStopPollingRef.current) {
            return; // Exit early, don't update any status
          }
          
          // Don't override status messages from pixel streaming POST payload
          // Once we receive pixel streaming data, those status messages are more accurate
          // and come directly from the EC2 instance startup process
          if (pixelStreamingDataReceivedRef.current) {
            // Pixel streaming data is being received - don't override status messages
            // The status messages from POST /api/pixelstreaming/ready are more accurate
            // Only update step if needed, but don't change status message
            if (currentState === 'pending' || currentState === 'initializing' || currentState === 'starting') {
              setCurrentStep('starting');
            } else if (currentState === 'running' && statusChecksPassed < 3) {
              setCurrentStep('health_check');
            } else if (currentState === 'running' && statusChecksPassed === 3) {
              setCurrentStep('booting');
            }
            // Continue polling for instance readiness, but don't update status message
            // Status message will come from pixel streaming POST payload
            if (!shouldStopPollingRef.current) {
              pollTimeoutRef.current = setTimeout(pollForReady, pollInterval);
            }
            return;
          }
          
          // Show appropriate message based on actual state and status checks
          // (Only when pixel streaming data hasn't been received yet)
          if (currentState === 'pending' || currentState === 'initializing') {
            setCurrentStep('starting');
            setStatusMessage('3D Viewer is starting...');
            runningStateFirstSeen = null; // Reset if state changes
          } else if (currentState === 'starting') {
            setCurrentStep('booting');
            setStatusMessage('3D Viewer is starting...');
            runningStateFirstSeen = null; // Reset if state changes
          } else if (currentState === 'stopped') {
            setCurrentStep('starting');
            setStatusMessage('3D Viewer is starting...');
            runningStateFirstSeen = null; // Reset if state changes
          } else if (currentState !== 'running') {
            // Any other state (stopping, shutting-down, etc.)
            setCurrentStep('starting');
            setStatusMessage('3D Viewer is starting...');
            runningStateFirstSeen = null; // Reset if state changes
          } else if (currentState === 'running' && statusChecksPassed < 3) {
            // 3D Viewer is running but status checks are not all passed yet
            if (systemCheck === 'initializing' || instanceCheck === 'initializing' || 
                systemCheck === 'not-applicable' || instanceCheck === 'not-applicable') {
              setCurrentStep('health_check');
              setStatusMessage('3D Viewer is starting...');
            } else {
              setCurrentStep('health_check');
              setStatusMessage('3D Viewer is starting...');
            }
            runningStateFirstSeen = null; // Reset - not ready yet
          } else {
            // Fallback for any other case
            setCurrentStep('booting');
            setStatusMessage('3D Viewer is starting...');
          }
          
          // Check if instance is in "running" state AND has all status checks passed (3/3)
          // This is the true indicator that the instance is ready
          const checkResultState = checkResult.status?.state || 'unknown';
          const allChecksPassed = statusChecksPassed === 3 && isHealthy;
          
          // Only proceed if state is "running" AND all status checks are passed (3/3)
          // This ensures we only show "running" message when instance is truly ready
          if (checkResult.success && checkResultState === 'running' && allChecksPassed) {
            const now = Date.now();
            
            // Track when we first see "running" state with all checks passed
            if (runningStateFirstSeen === null) {
              runningStateFirstSeen = now;
              console.log(`🟡 Instance ${projectId} first detected with all status checks passed (3/3), waiting ${MIN_RUNNING_DURATION/1000}s to confirm...`);
              // Continue polling - don't start countdown yet
              if (!shouldStopPollingRef.current) {
                pollTimeoutRef.current = setTimeout(pollForReady, pollInterval);
              }
              return;
            }
            
            // Check if 3D Viewer has been in "running" state with all checks passed for minimum duration
            const runningDuration = now - runningStateFirstSeen;
            if (runningDuration < MIN_RUNNING_DURATION) {
              // Still waiting for minimum duration
              console.log(`⏳ 3D Viewer ${projectId} with all checks passed for ${Math.floor(runningDuration/1000)}s, need ${Math.ceil((MIN_RUNNING_DURATION - runningDuration) / 1000)}s more to confirm...`);
              if (!shouldStopPollingRef.current) {
                setStatusMessage('3D Viewer is starting...');
                pollTimeoutRef.current = setTimeout(pollForReady, pollInterval);
              }
              return;
            }
            
            // 3D Viewer has been in "running" state with all checks passed for at least MIN_RUNNING_DURATION - now we can trust it
            console.log(`✅ 3D Viewer ${projectId} confirmed ready: running state + all checks passed (3/3) for ${Math.floor(runningDuration/1000)}s`);
            // Instance is ready - progress bar will reach 100% naturally after 3 minutes
            // Stop polling since we know it's ready
            if (pollTimeoutRef.current) {
              clearTimeout(pollTimeoutRef.current);
              pollTimeoutRef.current = null;
            }
            return;
          }
          
          // Check if we've exceeded max attempts
          if (pollAttempts >= maxPollAttempts) {
            throw new Error('3D Viewer did not become ready within the expected time. Please try again.');
          }
          
          // Continue polling only if we haven't stopped
          if (!shouldStopPollingRef.current) {
            pollTimeoutRef.current = setTimeout(pollForReady, pollInterval);
          }
          
        } catch (pollError) {
          console.error('Error polling for instance status:', pollError);
          
          // If it's a network error and we haven't exceeded attempts, retry
          if (!shouldStopPollingRef.current && pollAttempts < maxPollAttempts && (pollError.code === 'ECONNABORTED' || pollError.code === 'ETIMEDOUT')) {
            pollTimeoutRef.current = setTimeout(pollForReady, pollInterval);
            return;
          }
          
          // Otherwise, show error
          const errorMessage = pollError.response?.data?.message || pollError.message || 'Error starting 3D Viewer';
          setStatusMessage(`Error: ${errorMessage}`);
          onError(new Error(errorMessage));
        }
      };
      
      // Start polling after a short delay (only if not stopped)
      if (!shouldStopPollingRef.current) {
        pollTimeoutRef.current = setTimeout(pollForReady, pollInterval);
      }
      
    } catch (error) {
      // Extract error details from either axios error or manually thrown error
      const responseData = error.response?.data;
      const backendResponse = error.backendResponse;
      
      // Extract error message - prioritize actual AWS/backend error details
      // The backend error field often contains more detailed information than the message field
      let errorMessage = 'Unknown error occurred while starting 3D Viewer';
      
      if (backendResponse?.error) {
        // Backend error field contains the actual AWS/technical error
        // Try to extract a user-friendly message from it
        const backendError = backendResponse.error;
        if (backendError.includes('VcpuLimitExceeded')) {
          errorMessage = 'AWS vCPU limit exceeded. You have reached your EC2 instance vCPU limit. Please contact AWS support to request an increase, or stop other running instances.';
        } else if (backendError.includes('InsufficientInstanceCapacity')) {
          errorMessage = 'Insufficient instance capacity. The requested instance type is not available in this region. Please try again later or use a different instance type.';
        } else if (backendError.includes('InstanceLimitExceeded')) {
          errorMessage = 'Instance limit exceeded. You have reached the maximum number of running instances. Please stop other instances before starting a new one.';
        } else {
          // Use the backend error as-is, but clean it up a bit
          errorMessage = backendError.replace(/An error occurred \([^)]+\) when calling the [^:]+: /, '');
        }
      } else if (backendResponse?.message) {
        errorMessage = backendResponse.message;
      } else if (responseData?.error) {
        errorMessage = responseData.error;
      } else if (responseData?.message) {
        errorMessage = responseData.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Enhanced error logging with full context (log separately to avoid truncation)
      console.error('❌ Error starting instance:', errorMessage);
      console.error('📋 Project ID:', error.projectId || projectId);
      console.error('🖥️ Instance ID:', error.instanceId || backendResponse?.instance_id);
      console.error('📛 Instance Name:', error.instanceName || backendResponse?.instance_name);
      
      if (backendResponse) {
        console.error('🔙 Backend Response:', JSON.stringify(backendResponse, null, 2));
      }
      
      if (responseData) {
        console.error('📡 Axios Response Data:', JSON.stringify(responseData, null, 2));
      }
      
      if (error.response) {
        console.error('🌐 HTTP Status:', error.response.status, error.response.statusText);
      }
      
      if (error.code) {
        console.error('🔢 Error Code:', error.code);
      }
      
      if (error.troubleshooting || backendResponse?.troubleshooting) {
        console.error('💡 Troubleshooting Tips:', error.troubleshooting || backendResponse.troubleshooting);
      }
      
      if (error.stack) {
        console.error('📚 Stack Trace:', error.stack);
      }
      
      // Log full error object for complete context
      console.error('🔍 Full Error Object:', error);
      
      // Preserve troubleshooting and status details if available
      const enhancedError = new Error(errorMessage);
      
      // Preserve backend response data
      if (backendResponse) {
        enhancedError.backendResponse = backendResponse;
        if (backendResponse.troubleshooting) {
          enhancedError.troubleshooting = backendResponse.troubleshooting;
        }
        if (backendResponse.status_details) {
          enhancedError.status_details = backendResponse.status_details;
        }
        if (backendResponse.instance_id) {
          enhancedError.instanceId = backendResponse.instance_id;
        }
        if (backendResponse.instance_name) {
          enhancedError.instanceName = backendResponse.instance_name;
        }
      }
      
      // Preserve axios response data
      if (responseData?.troubleshooting) {
        enhancedError.troubleshooting = responseData.troubleshooting;
      }
      if (responseData?.status_details) {
        enhancedError.status_details = responseData.status_details;
      }
      
      // Preserve error properties from thrown error
      if (error.troubleshooting) {
        enhancedError.troubleshooting = error.troubleshooting;
      }
      if (error.status_details) {
        enhancedError.status_details = error.status_details;
      }
      if (error.projectId) {
        enhancedError.projectId = error.projectId;
      }
      if (error.instanceId) {
        enhancedError.instanceId = error.instanceId;
      }
      if (error.instanceName) {
        enhancedError.instanceName = error.instanceName;
      }
      
          // Error handling - no progress interval to clear
      setStatusMessage(`Error: ${errorMessage}`);
      onError(enhancedError);
    }
  };


  return (
    <div className={`ec2-loading-bar ${currentStep === 'stopping' ? 'stopping' : ''}`}>
      <div className="ec2-loading-content">
        <div className="ec2-loading-left">
          <div className="ec2-loading-spinner">
            <div className="spinner-circle"></div>
          </div>
          <span className="ec2-status-text">{statusMessage}</span>
        </div>
        
        <div className="ec2-progress-bar-container">
          <div className="ec2-progress-bar">
            <div 
              className="ec2-loading-bar-progress-fill"
              style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
            ></div>
          </div>
          <span className="ec2-percentage-text">{Math.round(Math.max(0, Math.min(100, progress)))}%</span>
        </div>
      </div>
    </div>
  );
};

export default EC2LoadingBar;

